import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Truck, Download, Calendar, XCircle, RefreshCcw } from "lucide-react";

const LABEL_TONE = {
  shipped: "bg-blue-50 text-blue-700 border-blue-200",
  delivered: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  awb_assigned: "bg-purple-50 text-purple-700 border-purple-200",
  awb_pending: "bg-yellow-50 text-yellow-800 border-yellow-200",
  packed: "bg-purple-50 text-purple-700 border-purple-200",
};

export default function ShipmentControls({ order, vendorId, canCancel = false, onChange }) {
  const [busy, setBusy] = useState(false);
  const [pickupDate, setPickupDate] = useState("");
  // Ignore empty records (shipment_id 0) from a push Shiprocket rejected: show "Ship via Shiprocket" instead.
  const shipment = (order.shipments || []).find((s) => s.vendor_id === vendorId && s.shipment_id) || null;

  const ship = async () => {
    setBusy(true);
    try {
      await api.post(`/exhibitor/orders/${order.id}/ship-via-shiprocket`, {});
      toast.success("Shipment created on Shiprocket");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create shipment");
    } finally { setBusy(false); }
  };

  const pickup = async () => {
    setBusy(true);
    try {
      await api.post(`/exhibitor/orders/${order.id}/shipment/pickup`, { pickup_date: pickupDate || undefined });
      toast.success("Pickup scheduled");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule pickup");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this shipment on Shiprocket?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/shipment/cancel`);
      toast.success("Shipment cancelled");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); }
  };

  if (!shipment) {
    return (
      <div className="mt-4 p-3 rounded-xl bg-[#FEF3E6] border border-[#F48E2E]/30 flex flex-wrap items-center justify-between gap-3" data-testid={`shipment-cta-${order.id}`}>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#F48E2E]">Shiprocket</div>
          <div className="text-xs text-gray-600 mt-0.5">Mark this order ready to ship. Creates a Shiprocket shipment, assigns AWB and generates the label.</div>
        </div>
        <Button data-testid={`shipment-ship-${order.id}`} onClick={ship} disabled={busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white h-9 text-xs">
          <Truck className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Creating…" : "Ship via Shiprocket"}
        </Button>
      </div>
    );
  }

  const tone = LABEL_TONE[shipment.status] || "bg-gray-50 text-gray-700 border-gray-200";
  return (
    <div className="mt-4 p-3 rounded-xl bg-white border border-gray-200 space-y-3" data-testid={`shipment-panel-${order.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-widest text-[#F48E2E]">Shiprocket</span>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${tone}`} data-testid={`shipment-status-${order.id}`}>
          {shipment.shiprocket_status_label || shipment.status}
        </span>
        {shipment.pickup_nickname && <span className="text-[11px] text-gray-500">Pickup: <span className="font-mono-tab">{shipment.pickup_nickname}</span></span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-gray-500">Courier</div>
          <div className="font-medium">{shipment.courier_name || "Awaiting assignment"}</div>
        </div>
        <div>
          <div className="text-gray-500">AWB</div>
          <div className="font-mono-tab">{shipment.awb_code || "—"}</div>
        </div>
        <div>
          <div className="text-gray-500">Shipment ID</div>
          <div className="font-mono-tab">{shipment.shipment_id}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {shipment.label_url && (
          <a data-testid={`shipment-label-${order.id}`} href={shipment.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#F48E2E] hover:underline">
            <Download className="h-3.5 w-3.5" /> Download label
          </a>
        )}
        {shipment.awb_code && (
          <a data-testid={`shipment-track-${order.id}`} href={`https://shiprocket.co/tracking/${shipment.awb_code}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#F48E2E]">
            <RefreshCcw className="h-3.5 w-3.5" /> Live tracking
          </a>
        )}
      </div>

      {shipment.awb_code && !shipment.pickup_requested_at && shipment.status !== "cancelled" && shipment.status !== "delivered" && (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="text-[11px] text-gray-500">Pickup date <span className="text-gray-400">(optional)</span></div>
            <Input data-testid={`shipment-pickup-date-${order.id}`} type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className="h-8 text-xs w-40" />
          </div>
          <Button data-testid={`shipment-pickup-${order.id}`} onClick={pickup} disabled={busy} className="rounded-full h-8 text-xs bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
            <Calendar className="h-3.5 w-3.5 mr-1.5" /> Schedule pickup
          </Button>
        </div>
      )}
      {shipment.pickup_requested_at && (
        <div className="text-[11px] text-gray-600">Pickup requested {new Date(shipment.pickup_requested_at).toLocaleString("en-IN")}{shipment.pickup_token ? ` · token ${shipment.pickup_token}` : ""}</div>
      )}

      {canCancel && shipment.status !== "cancelled" && shipment.status !== "delivered" && (
        <Button data-testid={`shipment-cancel-${order.id}`} onClick={cancel} disabled={busy} variant="outline" className="rounded-full h-8 text-xs text-red-600 border-red-200 hover:bg-red-50">
          <XCircle className="h-3.5 w-3.5 mr-1.5" /> Cancel shipment
        </Button>
      )}
    </div>
  );
}
